import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Calendar, Mail } from "lucide-react";
import CopyLinkButton from "./CopyLinkButton";
import { siteUrl } from "../lib/siteConfig";

// Renders the full Foundation for Change Circle (FCC) narrative on /foundation.
// Content sourced verbatim from the user's FCC Foundation_for_Change document.
export default function FCCSection() {
  return (
    <section
      id="fcc"
      data-testid="fcc-section"
      className="bg-[#fdfbf7]"
    >
      {/* CHAPTER DIVIDER — signals a shift from "what we do" to "how you can be part of it" */}
      <div
        data-testid="fcc-chapter-divider"
        className="bg-[#fdfbf7] border-t border-black/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <div className="flex flex-col items-center text-center">
            <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-[#4a4a4a]">
              The next chapter
            </span>
            <div className="mt-4 flex items-center gap-6">
              <span className="h-px w-16 md:w-24 bg-[#1a1a1a]/20" />
              <span className="font-serif text-3xl md:text-4xl italic text-[#1a1a1a]">
                II
              </span>
              <span className="h-px w-16 md:w-24 bg-[#1a1a1a]/20" />
            </div>
            <p className="mt-4 font-serif text-2xl md:text-3xl lg:text-4xl leading-snug tracking-tight text-[#1a1a1a] max-w-3xl">
              You have seen the work.
              <br />
              <em className="italic">Now walk with us</em>, story to story.
            </p>
          </div>
        </div>
      </div>

      {/* INTRO */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-8 md:pt-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase font-bold text-[#2d4a22]">
              The Foundation for Change Circle (FCC)
            </p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1a1a1a]">
              Join the
              <br />
              <em className="italic">Circle</em>.
            </h2>
            <p className="mt-8 font-mono text-base leading-relaxed text-[#1a1a1a]">
              We build stories that create real-world impact. A story is not
              complete when it&rsquo;s told — it is complete when it creates
              change.
              <br />
              <br />
              Join us in making that change possible.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#donate"
                data-testid="fcc-join-cta"
                className="inline-flex items-center gap-3 bg-[#1a1a1a] text-[#fdfbf7] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#2d2d2d] transition-colors"
              >
                Become a member
                <ArrowRight size={16} strokeWidth={1.4} />
              </a>
              <CopyLinkButton
                url={siteUrl("/fcc")}
                label="Copy FCC link"
                variant="dark"
                testId="copy-fcc-link"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="font-sans text-[17px] md:text-[18px] leading-[1.85] text-[#1a1a1a] space-y-7">
              <p>
                Through Stories by Aradhana, we have had the privilege of
                sharing hundreds of stories of courage, resilience, and hope.
                But for us, a story does not end when the video ends. Many of
                the people whose journeys we have documented continue to need
                support long after their stories are shared. Whether it is
                medical expenses, monthly ration, rehabilitation, emergency
                assistance, or simply ensuring that they are doing okay — the
                responsibility often continues for months and sometimes years.
              </p>
              <p>
                Human stories do not follow a timeline. Medical treatments
                continue, monthly ration is needed every month, emergencies
                arise unexpectedly, and follow-up visits often happen long
                after a video is published. A recurring community allows us
                to respond consistently instead of only when an emergency
                campaign is launched.
              </p>
              <p>
                Many of you have written to us over the years asking if there
                was a way to support both the individuals whose stories we
                feature and the team that works behind the scenes to find,
                document and continue supporting them. The{" "}
                <strong>Foundation for Change Circle (FCC)</strong> has been
                created with exactly that intention.
              </p>
              <p>
                FCC is not a one-time donation initiative, but a{" "}
                <strong>recurring support system</strong> that allows
                continuous care, timely intervention, and long-term follow-up
                for the people we document.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* HOW THE FUNDS ARE USED */}
      <div className="bg-[#2d4a22] text-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                How the funds are used
              </p>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                A minimum of 75% of every contribution goes directly to
                beneficiaries.
              </h3>
              <p className="mt-8 font-sans text-[17px] leading-relaxed text-[#fdfbf7]/85 max-w-md">
                Depending on the needs of the Foundation, this percentage is
                often significantly higher. This is a floor, not a ceiling.
              </p>
              <Link
                to="/how-your-donation-is-used"
                data-testid="fcc-transparency-link"
                className="mt-8 sba-link-arrow font-sans text-sm tracking-[0.15em] uppercase text-[#f9d94b]"
              >
                Full transparency report
                <span className="arrow">
                  <ArrowRight size={16} strokeWidth={1.4} />
                </span>
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-10">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                    Direct beneficiary support
                  </p>
                  <ul className="mt-5 space-y-3 font-sans text-[15px] text-[#fdfbf7]/85">
                    {[
                      "Medical expenses",
                      "Monthly ration and basic necessities",
                      "Emergency assistance",
                      "Rehabilitation and follow-up support",
                      "Other essential needs over time",
                    ].map((x) => (
                      <li key={x} className="pl-5 relative">
                        <span className="absolute left-0">—</span>
                        {x}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 font-sans text-sm text-[#fdfbf7]/65 leading-relaxed">
                    People like{" "}
                    <span className="italic">Deepali Ghosh, Dilip Ganguly</span>{" "}
                    and <span className="italic">Mansukh Kaka</span> often
                    require continued support long after their stories have
                    been shared.
                  </p>
                </div>

                <div>
                  <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                    To find &amp; document more stories
                  </p>
                  <ul className="mt-5 space-y-3 font-sans text-[15px] text-[#fdfbf7]/85">
                    {[
                      "Travel and logistics",
                      "Research and verification",
                      "Production and editing",
                      "Team and operational expenses",
                      "Follow-up visits and impact tracking",
                      "Reaching more people across India",
                    ].map((x) => (
                      <li key={x} className="pl-5 relative">
                        <span className="absolute left-0">—</span>
                        {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* IMPORTANT CLARIFICATION */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d4a22]">
              An important clarification
            </p>
            <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight text-[#1a1a1a]">
              FCC and crowdfunding campaigns are <em className="italic">separate</em>.
            </h3>
          </div>
          <div className="lg:col-span-7 font-sans text-[16px] md:text-[17px] leading-[1.85] text-[#1a1a1a]">
            <p>
              FCC is completely separate from the crowdfunding campaigns we
              occasionally raise for specific individuals featured in our
              videos. Whenever we raise funds for a particular person or
              cause, <strong>100%</strong> of those funds will continue to be
              used solely for that purpose and will remain separately
              accounted for.
            </p>
            <p className="mt-5">
              FCC exists to help us provide continued support to people whose
              stories we have already documented, while also helping us
              sustain the work that goes into finding, documenting and
              supporting many more.
            </p>
          </div>
        </div>
      </div>

      {/* WHAT MEMBERS RECEIVE */}
      <div className="bg-[#1a1a1a] text-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
              What members of the Circle receive
            </p>
            <h3 className="mt-6 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              You become part of the journey behind these stories.
            </h3>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-10 gap-x-12">
            {[
              {
                icon: Heart,
                title: "Follow-ups and impact updates",
                body: "See how the people you supported are doing, months and years after the video.",
              },
              {
                icon: Sparkles,
                title: "Behind-the-scenes from our shoots",
                body: "Glimpses, voice notes, raw clips and the real human moments we don't share publicly.",
              },
              {
                icon: Calendar,
                title: "Half-yearly meetups",
                body: "Lunches and dinners with the team and other Circle members — small, warm, in-person.",
              },
              {
                icon: Mail,
                title: "Early access to ongoing stories",
                body: "First-look updates on stories we're researching, filming or following up on.",
              },
              {
                icon: Sparkles,
                title: "Periodic impact reports",
                body: "Plain, honest reports showing how every rupee was used and the impact created.",
              },
              {
                icon: Heart,
                title: "Income Tax Exemption certificate",
                body: "Shared as per the applicable provisions of the Income Tax Act.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="border-t border-[#fdfbf7]/15 pt-6"
                data-testid="fcc-benefit-card"
              >
                <Icon size={22} strokeWidth={1.4} className="text-[#f9d94b]" />
                <h4 className="mt-5 font-serif text-xl leading-tight tracking-tight">
                  {title}
                </h4>
                <p className="mt-3 font-sans text-[15px] leading-relaxed text-[#fdfbf7]/70">
                  {body}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-16 font-serif italic text-xl md:text-2xl leading-snug text-[#fdfbf7]/85 max-w-2xl">
            &ldquo;Most importantly — you become a part of the journey behind
            these stories and the impact they create.&rdquo;
          </p>
        </div>
      </div>

      {/* FOUNDATION DETAILS */}
      <div className="bg-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d4a22]">
                Foundation details
              </p>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight text-[#1a1a1a]">
                A registered{" "}
                <span className="underline decoration-[#f9d94b] decoration-[6px] underline-offset-[8px]">
                  Section 8
                </span>{" "}
                company.
              </h3>
            </div>
            <div className="lg:col-span-7 font-sans text-[16px] md:text-[17px] leading-[1.85] text-[#1a1a1a]">
              <p>
                <strong>Stories by Aradhana Foundation for Change</strong> is a
                registered <strong>Section 8 company</strong> committed to
                creating positive social impact through storytelling and
                sustained community support.
              </p>
              <p className="mt-5">
                The Foundation is registered under{" "}
                <strong>Section 332 (erstwhile Section 12A)</strong> of the
                Income Tax Act, and donations made to the Foundation are
                eligible for tax benefits under{" "}
                <strong>Section 354 (erstwhile Section 80G)</strong>, subject
                to applicable laws.
              </p>
              <p className="mt-5 pt-5 border-t border-[#1a1a1a]/10 font-mono text-[11px] tracking-[0.2em] uppercase text-[#4a4a4a]">
                Living abroad?{" "}
                <Link
                  to="/patrons-monthly"
                  data-testid="fcc-intl-patron-link"
                  className="underline underline-offset-4 decoration-[#f9d94b] decoration-2 text-[#1a1a1a] hover:decoration-[3px]"
                >
                  Support us as an International Patron →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
